//

import moment from "moment";
import { PublishDetailType, PublishHeaderType, PublishEviewResultType, PublishStateType, RawItemType } from "./types";

function debounce<T extends (...args: any[]) => any>(func: T, wait: number = 100): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

const fillUpZero = (nr: number): string => nr < 10 ? `0${nr}` : `${nr}`;

function shortDateTime(dt: Date | undefined, utc: boolean = true): string {
  if (!dt) return "";

  const date = moment.isMoment(dt) ? dt.toDate() : dt;
  const month = utc ? dt.getUTCMonth() : dt.getMonth();
  const day = utc ? dt.getUTCDate() : dt.getDate();
  const hours = utc ? dt.getUTCHours() : dt.getHours();
  const minutes = utc ? dt.getUTCMinutes() : dt.getMinutes();
  const seconds = utc ? dt.getUTCSeconds() : dt.getSeconds();

  return `${dt.getFullYear()}/${fillUpZero(month + 1)}/${fillUpZero(day)} ${fillUpZero(hours)}:${fillUpZero(minutes)}:${fillUpZero(seconds)}`;
}

function formatDuration(d1: Date, d2: Date | undefined): string {
  if (!d1 || !d2) return "";

  let totalSeconds = Math.floor((d2.getTime() - d1.getTime()) / 1000);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);

  return `${fillUpZero(hours)}:${fillUpZero(minutes)}:${fillUpZero(seconds)}`;
}

function calcPublishResult(items: RawItemType[]) {
  const publishMap = new Map<string, RawItemType[]>();
  items.forEach((item) => {
    if (!publishMap.has(item.publishId)) {
      publishMap.set(item.publishId, []);
    }
    publishMap.get(item.publishId)!.push(item);
  });

  const publishTuples = Array.from(publishMap, ([publishId, items]) => ({ publishId, items }));

  publishTuples.forEach((tuple) => {
    tuple.items.sort((a, b) => a.timeStamp.getTime() - b.timeStamp.getTime());
  });

  const result: PublishEviewResultType[] = [];
  publishTuples.forEach((tuple) => {
    const getMessage = (message: string) => tuple.items.find((i) => i.message.startsWith(message));

    const firstItem = tuple.items[0];
    let state: PublishStateType = "";
    let finishDate = undefined;

    let finishMessage = tuple.items.find((i) => i.message.startsWith("PublishProject. message:HandleMessage finish"));
    if (finishMessage) {
      state = "finished";
      finishDate = finishMessage.timeStamp;
    } else {
      // if no valid finish message found, than take the last message
      const lastMessage = tuple.items[tuple.items.length - 1];
      finishDate = lastMessage.timeStamp;

      if (getMessage("PublishProject. message:HandleMessage project not found publishId")) {
        state = "projectNotFound";
      }

      if (getMessage("PublishProject. message:Exception")) {
        state = "exception";
      }
    }

    const startHandleMessages = tuple.items.filter(
      (i) =>
        i.message.startsWith("PublishProject. message:HandleOneMessage start processor publishId") ||
        i.message.startsWith("PublishProject. message:HandleMessage start processor publishId")
    );

    const fileSizeMessage = tuple.items.find(
      (i) =>
        i.message.startsWith("PublishProject. message:HandleMessage eppFileName file size ")
    );

    var eppFileSize = 0;

    if (fileSizeMessage) {
      const msgParts = fileSizeMessage.message.replace("PublishProject. message:HandleMessage eppFileName file size ", "").split(" ", 1);
      const sizePart = msgParts[0];
      if (sizePart) {
        eppFileSize = parseFloat(sizePart);
      }
    }

    const languagesMessage = tuple.items.find(
      (i) =>
        i.message.startsWith("PublishProject. message:HandleMessage Project have ")
    );

    var languagesCnt = 0;

    if (languagesMessage) {
      const msgParts = languagesMessage.message.replace("PublishProject. message:HandleMessage Project have ", "").split(" ", 1);
      const sizePart = msgParts[0];
      if (sizePart) {
        languagesCnt = parseInt(sizePart);
      }
    }

    var startPublishMessage = tuple.items.find((i) =>
      i.message.startsWith("PublishProject. message:Controller start publishId:")
    );

    if (!startPublishMessage) {
      startPublishMessage = tuple.items.find((i) =>
        i.message.startsWith("PublishProject. message:Controller start PublishParametersEpp: ")
      );

       if (startPublishMessage) {
        var jsonStr = startPublishMessage.message.substring("PublishProject. message:Controller start PublishParametersEpp: ".length, startPublishMessage?.message.indexOf("}}")+2).trim() || "";
        try {
          var orgPos = jsonStr.indexOf("OrganizationId: \"");
          var endPos = jsonStr.indexOf("\",", orgPos);
          var orgIdStr = jsonStr.substring(orgPos, endPos) || "";
          startPublishMessage.organizationId = orgIdStr.replace("OrganizationId: \"", "").trim();
        }
        catch (e) {
          console.warn("Failed to parse PublishParametersEpp JSON", e);
          // ignore JSON parse error
        }
      }
    }

    if (!startPublishMessage) {
      startPublishMessage = tuple.items.find((i) =>
        i.message.startsWith("PublishProject. message:Controller start next language publishId"));
    }

    const projectFoundMessage = tuple.items.find((i) =>
      i.message.startsWith("PublishProject. message:HandleMessage project found publishId")
    );

    const addDataMessage = tuple.items.find((i) =>
      i.message.startsWith("PublishProject. message:HandleMessage addData publishId")
    );

    const readProjectMessage = tuple.items.find((i) =>
      i.message.startsWith("PublishProject. message:Controller read project.json publishId")
    );

    const name = projectFoundMessage?.name || startPublishMessage?.name;
    const folder = projectFoundMessage?.folder || startPublishMessage?.folder;
    const organizationId = startPublishMessage?.organizationId || "";
    const fileCount = readProjectMessage?.fileCount ?? addDataMessage?.fileCount;
    const language = startPublishMessage?.language || languagesCnt.toString();
    const publishType = startPublishMessage?.publishType || "";
    const operationId = startPublishMessage?.operationId || "";

    const header: PublishHeaderType = {
      state: state,
      publishId: tuple.publishId,
      startDate: firstItem.timeStamp,
      finishDate: finishDate,
      handleCount: startHandleMessages.length,
      name: name || "",
      folder: folder || "",
      organizationId: organizationId || "",
      eppSize: eppFileSize,
      fileCount: fileCount ?? 0,
      language,
      publishType,
      isRecent: false,
      operationId: operationId,
    };
    const details: PublishDetailType[] = tuple.items.map((i) => ({
      timeStamp: i.timeStamp,
      message: i.message,
    }));

    result.push({ header, details });
  });
  return result;
}

function getOrganizationIds(results: PublishEviewResultType[]): string[] {
  return Array.from(new Set(results.map((r) => r.header.organizationId || "")));
}

function getHostname(message: string): string | null {
  const idx = message.indexOf("Hostname:");
  if (idx > 0) {
    const tok = message.substring(idx).split(" ")[0].split(":");
    if (tok.length > 0) {
      return tok[1];
    }
    return null;
  }
  return null;
}

function isTimestampFromLast2Minutes(finishDate: Date): boolean {
  return getMinutesDiff(Date.now(), finishDate.getTime()) < 2;
}

function getMinutesDiff(timestamp1: number, timestamp2: number) {
  let difference = timestamp1 - timestamp2;
  let minsDifference = difference / 1000 / 60;

  return minsDifference;
}

const ONE_DAY_MS = 1000 * 60 * 60 * 24;
const getDateMinusOneDay = (date: Date): Date => new Date(date.getTime() - ONE_DAY_MS);
const getDatePlusOneDay = (date: Date): Date => new Date(date.getTime() + ONE_DAY_MS);

export {
  debounce,
  shortDateTime,
  formatDuration,
  calcPublishResult,
  getOrganizationIds,
  getHostname,
  isTimestampFromLast2Minutes,
  getMinutesDiff,
  getDateMinusOneDay,
  getDatePlusOneDay,
};
